import ConfirmationDialog from '@/components/common/ConfirmationDialog';
import { setCurrentChatId } from '@/redux/slices/chatSlice';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { chatSelectOneObjectByQuery, DBChatThunks } from '@/redux/thunks/DBServiceThunks';
import {
  Clock,
  FileSearch,
  MessageSquare,
  Trash2,
  Loader2
} from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';

type Props = {
  chatId: string;
}

export default function ChatItem({ chatId }: Props) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const currentChatId = useAppSelector((state) => state.ChatReducer.currentChatId);
  const chat = useAppSelector((state) => chatSelectOneObjectByQuery(state.DBChatReducer, { _id: chatId }));

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<{ _id: string, title: string } | null>(null);
  const [isSelectingChat, setIsSelectingChat] = useState(false);

  const isCurrentChat = currentChatId === chatId;
  const isDocumentSearch = chat?.type === 'documentSearch';

  const handleSelectChat = async () => {
    if (!isSelectingChat) {
      setIsSelectingChat(true);
      try {
        dispatch(setCurrentChatId(chatId));
      } finally {
        setIsSelectingChat(false);
      }
    }
  };

  const handleDeleteRequest = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (chat) {
      setChatToDelete({
        _id: chatId,
        title: chat.title
      });
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (chatToDelete) {
      if (chatToDelete._id === currentChatId) {
        dispatch(setCurrentChatId(null));
      }
      dispatch(DBChatThunks.delete({ entity: { _id: chatToDelete._id } }));
      setDeleteDialogOpen(false);
      setChatToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setChatToDelete(null);
  };

  return (
    <>
      <Card
        onClick={isSelectingChat ? undefined : handleSelectChat}
        className={`
          p-4 cursor-pointer transition-smooth relative h-[88px] flex flex-col justify-between
          ${isCurrentChat
            ? 'border-gold/50 bg-gold/8 shadow-md border-l-[3px] border-l-gold'
            : 'border-border hover:border-gold/40 hover:bg-accent/50 hover:shadow-sm'
          }
          ${isSelectingChat && 'opacity-70 cursor-default'}
          ${isDocumentSearch && 'border-l-2 border-l-amber-500'}
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`flex items-center justify-center rounded-lg p-2 shrink-0 ${isCurrentChat
              ? isDocumentSearch ? 'bg-amber-500 text-white' : 'bg-gold text-primary-foreground'
              : isDocumentSearch ? 'bg-amber-500/10 text-amber-600' : 'bg-gold/10 text-gold'
              }`}>
              {isDocumentSearch ? (
                <FileSearch className="w-4 h-4" />
              ) : (
                <MessageSquare className="w-4 h-4" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold truncate text-foreground">
                {chat?.title}
              </h2>
              <p className="text-xs text-muted-foreground truncate mt-0.5 h-[16px]">
                {isDocumentSearch ? t('components.documentSearch.searchedIn') : '\u00A0'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDeleteRequest}
            disabled={isSelectingChat}
            className="h-7 w-7 shrink-0 hover:bg-destructive/10 hover:text-destructive"
            title={t('common.tooltips.deleteConversation')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>

        <div className="flex items-center justify-between mt-3 ml-1">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>{new Date(chat?.updatedAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Loading Overlay */}
        {isSelectingChat && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-lg z-10">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-gold" />
              <span className="text-xs font-medium text-muted-foreground">
                {t('common.auth.loading')}
              </span>
            </div>
          </div>
        )}
      </Card >

      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t('common.tooltips.deleteConversation')}
        message={t('common.chat.deleteConversationConfirm')}
        itemName={chatToDelete?.title}
        confirmButtonText={t('common.buttons.delete')}
        isDestructive={true}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
}
